import i18n from 'i18next';
import PptxGenJS from 'pptxgenjs';

import Cover from 'src/assets/action-plan/cover.jpg';
import BrandLogo from 'src/assets/img/brand_logo.png';
import GetReportLogo from 'src/assets/img/DPDHL_Group_EOS_ENG_logo.png';
import { cutOffString } from 'src/helper/cutOffString';
import { getActionPlanStatus, getCurrentLocale } from 'src/helper/helper';

export const DPI = 72;

const dateStringOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

const getTranslator = () => {
  const currentLang = i18n.language;
  const translator = i18n.getFixedT(currentLang, ['common', 'planAction']);
  return [translator, currentLang];
};

const internalUseTextSettings = {
  x: '2%',
  h: '8%',
  fontSize: 8,
  color: '#8c8c8c',
  bold: true,
  fontFace: 'Delivery',
};

const createCoverSlide = (
  pptx,
  generatedDate,
  survey,
  orgUnit,
  title,
  subtitle,
  t,
) => {
  const coverSlide = pptx.addSlide();

  coverSlide.addImage({
    path: Cover,
    x: 0,
    y: 0,
    h: '100%',
    w: '100%',
  });

  coverSlide.addText(
    t('DownloadActionPlans.InternalUse'),
    internalUseTextSettings,
  );

  coverSlide.addImage({
    path: GetReportLogo,
    x: 0.375,
    y: 0.43,
    w: 0.625 * (GetReportLogo.width / GetReportLogo.height),
    h: 0.625,
  });

  coverSlide.addText(`${survey.name}:`, {
    x: 0.24,
    y: 2.5,
    fontSize: 24,
    color: '#d40511',
    fontFace: 'Delivery',
  });

  coverSlide.addText(title, {
    x: 0.24,
    y: 2.9,
    fontSize: 24,
    color: '#d40511',
    bold: true,
    fontFace: 'Delivery',
  });

  coverSlide.addText(`${orgUnit.org_unit_id} ${orgUnit.name}`, {
    x: 0.24,
    y: 3.22,
    fontSize: 18,
    color: '#d3050f',
    fontFace: 'Delivery',
  });

  if (subtitle) {
    coverSlide.addText(subtitle, {
      x: 0.24,
      y: 3.52,
      fontSize: 18,
      color: '#d3050f',
      fontFace: 'Delivery',
    });
  }

  coverSlide.addText(
    `${t('DownloadActionPlans.ExportedOn')}: ${generatedDate.toLocaleDateString(
      getCurrentLocale(),
      dateStringOptions,
    )}`,
    {
      x: 0.24,
      y: subtitle ? 3.82 : 3.5,
      fontSize: 12,
      color: '#000000',
      fontFace: 'Delivery',
    },
  );

  coverSlide.addImage({
    path: BrandLogo,
    x: 0.375,
    y: 4.986,
    w: 0.32 * (BrandLogo.width / BrandLogo.height),
    h: 0.32,
  });

  return pptx;
};

/**
 * Function for creating and downloading
 * a ppt of the three statistics tabs in
 * the planAction tab
 */
export const downloadPPT = ({
  additionalText,
  filterLine,
  firstImage,
  survey,
  orgUnit,
  secondImage,
  selectedHeader,
  thirdImage,
  top5KpiImage,
}) => {
  const [t] = getTranslator();
  const generatedDate = new Date();
  const pptx = new PptxGenJS();

  pptx.defineSlideMaster({
    title: 'SLIDE_MASTER',
    background: { color: 'FFFFFF' },
    objects: [
      {
        text: {
          text: t('DownloadActionPlans.InternalUse'),
          options: internalUseTextSettings,
        },
      },
      {
        text: {
          text: `${orgUnit.org_unit_id} ${orgUnit.name}${filterLine}`,
          options: {
            x: '25%',
            h: '8%',
            w: '64%',
            fontSize: 8,
            fontFace: 'Delivery',
            align: 'right',
          },
        },
      },
      {
        image: {
          path: GetReportLogo,
          x: 8.84,
          y: 0.18,
          w: 1,
          h: 0.61,
        },
      },
      {
        text: {
          text: selectedHeader.orgUnit.label,
          options: {
            x: '2%',
            h: '38%',
            fontSize: 8,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: cutOffString(selectedHeader.orgUnit.value, 30),
          options: {
            x: '2%',
            h: '44%',
            fontSize: 10,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: selectedHeader.responsibleManager.label,
          options: {
            x: '24%',
            h: '38%',
            fontSize: 8,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: cutOffString(selectedHeader.responsibleManager.value, 33),
          options: {
            x: '24%',
            h: '44%',
            fontSize: 10,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: selectedHeader.email.label,
          options: {
            x: '45%',
            h: '38%',
            fontSize: 8,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: cutOffString(selectedHeader.email.value, 27),
          options: {
            x: '45%',
            h: '44%',
            fontSize: 10,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: selectedHeader.priority.label,
          options: {
            x: '65%',
            h: '38%',
            fontSize: 8,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: additionalText.priorityValue,
          options: {
            x: '65%',
            h: '44%',
            fontSize: 10,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: selectedHeader.headcount.label,
          options: {
            x: '78%',
            h: '38%',
            fontSize: 8,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: selectedHeader.headcount.value,
          options: {
            x: '78%',
            h: '44%',
            fontSize: 10,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${survey.name}_${t(
            'DownloadActionPlans.EOSStatistics',
          )}_${orgUnit.org_unit_id}_${orgUnit.name}`,
          options: {
            x: '2%%',
            h: '95%',
            fontSize: 10,
            color: '#000000',
            valign: 'bottom',
            fontFace: 'Delivery',
          },
        },
      },
      {
        rect: {
          x: '3%',
          y: '25%',
          w: '85.4%',
          h: '62.4%',
          fill: { color: '#CCCCCC' },
        },
      },
      {
        rect: {
          x: '3.2%',
          y: '25.2%',
          w: '85%',
          h: '62%',
          fill: { color: '#FFFFFF' },
        },
      },
    ],
  });

  createCoverSlide(
    pptx,
    generatedDate,
    survey,
    orgUnit,
    t('DownloadActionPlans.EOSStatistics').toUpperCase(),
    undefined,
    t,
  );

  const firstSlide = pptx.addSlide({ masterName: 'SLIDE_MASTER' });
  firstSlide.addText(t('DownloadActionPlans.ExportPlanActionStatistics1'), {
    x: '2%',
    h: '25%',
    fontSize: 18,
    color: '#d3050f',
    bold: true,
    fontFace: 'Delivery',
  });
  firstSlide.addImage({
    data: firstImage.dataUrl,
    x: '6%',
    y: '30%',
    w: firstImage.width / DPI,
    h: firstImage.height / DPI,
    sizing: {
      type: 'contain',
      x: 0,
      y: 0,
      w: 3.19 * (firstImage.width / firstImage.height),
      h: 3.19,
    },
  });

  firstSlide.addText(`${additionalText.firstSlide}`, {
    x: '6.2%',
    y: '28%',
    fontSize: 12,
    color: '#000000',
    bold: true,
    fontFace: 'Delivery',
  });
  firstSlide.addText('2', {
    x: '90%',
    h: '95%',
    fontSize: 10,
    color: '#000000',
    valign: 'bottom',
    fontFace: 'Delivery',
  });

  const secondSlide = pptx.addSlide({ masterName: 'SLIDE_MASTER' });
  secondSlide.addText(t('DownloadActionPlans.ExportPlanActionStatistics2'), {
    x: '2%',
    h: '25%',
    fontSize: 18,
    color: '#d3050f',
    bold: true,
    fontFace: 'Delivery',
  });
  secondSlide.addImage({
    data: secondImage.dataUrl,
    x: 0.32,
    y: 1.42,
    w: secondImage.width / DPI,
    h: secondImage.height / DPI,
    sizing: {
      type: 'contain',
      x: 0,
      y: 0,
      w: 3.46 * (secondImage.width / secondImage.height),
      h: 3.46,
    },
  });

  secondSlide.addText('3', {
    x: '90%',
    h: '95%',
    fontSize: 10,
    color: '#000000',
    valign: 'bottom',
    fontFace: 'Delivery',
  });

  let ind = 0;
  thirdImage.forEach(({ dataUrl, height, width }, index) => {
    const thirdSlide = pptx.addSlide({ masterName: 'SLIDE_MASTER' });
    thirdSlide.addText(t('DownloadActionPlans.ExportPlanActionStatistics3'), {
      x: '2%',
      h: '25%',
      fontSize: 18,
      color: '#d3050f',
      bold: true,
      fontFace: 'Delivery',
    });

    const maxWidth = 8.375;
    const calculatedHeight = maxWidth * (height / width);
    const maxHeight = 246 / DPI;
    thirdSlide.addImage({
      data: dataUrl,
      x: 0.38,
      y: 1.45,
      w: width / DPI,
      h: height / DPI,
      sizing: {
        type: 'contain',
        x: 0,
        y: 0,
        w: maxWidth,
        h: calculatedHeight > maxHeight ? maxHeight : calculatedHeight,
      },
    });

    thirdSlide.addText(`${index + 4}`, {
      x: '90%',
      h: '95%',
      fontSize: 10,
      color: '#000000',
      valign: 'bottom',
      fontFace: 'Delivery',
    });
    ind = index;
  });

  const top5Slide = pptx.addSlide({ masterName: 'SLIDE_MASTER' });
  top5Slide.addText(t('DownloadActionPlans.ExportPlanActionStatistics3'), {
    x: '2%',
    h: '25%',
    fontSize: 18,
    color: '#d3050f',
    bold: true,
    fontFace: 'Delivery',
  });

  const {
    dataUrl: top5KpiImageDataUrl,
    height: top5KpiImageHeight,
    width: top5KpiImageWidth,
  } = top5KpiImage;
  top5Slide.addImage({
    data: top5KpiImageDataUrl,
    x: 0.38,
    y: 1.45,
    w: top5KpiImageWidth / DPI,
    h: top5KpiImageHeight / DPI,
    sizing: {
      type: 'contain',
      x: 0,
      y: 0,
      w: 8.375,
      h: 8.375 * (top5KpiImageHeight / top5KpiImageWidth),
    },
  });

  top5Slide.addText(`${ind + 5}`, {
    x: '90%',
    h: '95%',
    fontSize: 10,
    color: '#000000',
    valign: 'bottom',
    fontFace: 'Delivery',
  });

  // Create file and download it.
  pptx.writeFile({
    fileName: `planAction Statistics ${orgUnit.org_unit_id}.pptx`,
  });
};

/**
 * Function for creating and downloading
 * a ppt of an individual action plan in
 * the planAction tab
 */
export const downloadActionPlanPPT = ({
  actionData,
  actionGraphsImg,
  survey,
  orgUnit,
  planActionStatus,
}) => {
  const [t, currentLang] = getTranslator();
  const generatedDate = new Date();
  const updatedDate = new Date(actionData.updated);
  const pptx = new PptxGenJS();

  const actionPlanStatus = t(
    `planAction:ActionPlan.Status.${getActionPlanStatus(actionData.status)}`,
  );
  const manager =
    planActionStatus.value.manager !== null
      ? planActionStatus.value.manager
      : '';

  pptx.defineSlideMaster({
    title: 'SLIDE_MASTER',
    background: { color: 'FFFFFF' },
    objects: [
      {
        text: {
          text: t('DownloadActionPlans.InternalUse'),
          options: internalUseTextSettings,
        },
      },
      {
        text: {
          text: `${t(
            'DownloadActionPlans.UpdatedOn',
          )}: ${updatedDate.toLocaleDateString(
            getCurrentLocale(),
            dateStringOptions,
          )}`,
          options: {
            x: '70%',
            y: 0.16,
            fontSize: 8,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${t(
            'DownloadActionPlans.ExportedOn',
          )}: ${generatedDate.toLocaleDateString(
            getCurrentLocale(),
            dateStringOptions,
          )}`,
          options: {
            x: '70%',
            y: 0.28,
            fontSize: 8,
            fontFace: 'Delivery',
          },
        },
      },
      {
        image: {
          path: GetReportLogo,
          x: 8.84,
          y: 0.18,
          w: 1,
          h: 0.61,
        },
      },
      {
        text: {
          text: `${survey.year} ${t(
            'DownloadActionPlans.ActionPlanOverview',
          )}:`,
          options: {
            x: 0.24,
            y: 0.52,
            fontSize: 18,
            color: '#d3050f',
            bold: true,
            fontFace: 'Delivery',
          },
        },
      },
      {
        rect: {
          x: 0.305,
          y: 1.0416,
          w: 9.5416,
          h: 0.611,
          fill: { color: '#B2B2B2' },
        },
      },
      {
        rect: {
          x: 0.33,
          y: 1.0694,
          w: 9.4861,
          h: 0.55,
          fill: { color: '#F1F1F1' },
        },
      },
      {
        text: {
          text: `${t('ActionPlan.ActionPlanName')}:`,
          options: {
            x: 0.247,
            y: 1.169,
            fontSize: 8,
            color: '#000000',
            bold: true,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${actionData.name}`,
          options: {
            x: currentLang === 'de' ? 1.7 : 1.4,
            y: 1.169,
            align: 'left',
            margin: 0,
            fontSize: 8,
            color: '#000000',
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${t('ActionPlan.ActionPlanStatus')}:`,
          options: {
            x: 0.247,
            y: 1.35,
            fontSize: 8,
            color: '#000000',
            bold: true,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: actionPlanStatus,
          options: {
            x: currentLang === 'de' ? 1.7 : 1.4,
            y: 1.35,
            align: 'left',
            margin: 0,
            fontSize: 8,
            color: '#000000',
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${t('Header.Headcount')}`,
          options: {
            x: 0.247,
            y: 1.531,
            fontSize: 8,
            color: '#000000',
            bold: true,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${planActionStatus.value.headcount}`,
          options: {
            x: currentLang === 'de' ? 1.04 : 1.1,
            y: 1.531,
            align: 'left',
            margin: 0,
            fontSize: 8,
            color: '#000000',
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${t('Header.ResponsibleManager')}`,
          options: {
            x: 6.01,
            y: 1.169,
            fontSize: 8,
            color: '#000000',
            bold: true,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${manager.email !== undefined ? manager.email : ''}`,
          options: {
            x: currentLang === 'de' ? 7.78 : 7.35,
            y: 1.169,
            align: 'left',
            margin: 0,
            fontSize: 8,
            color: '#000000',
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${t('Header.Priority')}`,
          options: {
            x: 6.01,
            y: 1.35,
            fontSize: 8,
            color: '#000000',
            bold: true,
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${planActionStatus.value.priority ? t('yes') : t('no')}`,
          options: {
            x: currentLang === 'de' ? 6.65 : 6.65,
            y: 1.35,
            align: 'left',
            margin: 0,
            fontSize: 8,
            color: '#000000',
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: t('DownloadActionPlans.KPI'),
          options: {
            x: 0.24,
            y: 1.85,
            fontSize: 12,
            bold: true,
            color: '#000000',
            fontFace: 'Delivery',
          },
        },
      },
      {
        rect: {
          x: 0.35,
          y: 1.95,
          w: 2.84,
          h: 0.02,
          fill: { color: '#B2B2B2' },
        },
      },
      {
        rect: {
          x: 3.6,
          y: 1.95,
          w: 6.0,
          h: 0.02,
          fill: { color: '#B2B2B2' },
        },
      },
      {
        rect: {
          x: 3.6,
          y: 4.17,
          w: 6.0,
          h: 0.02,
          fill: { color: '#B2B2B2' },
        },
      },
      {
        rect: {
          x: 3.6,
          y: 4.2,
          w: 6.0,
          h: 1,
          fill: { color: '#B2B2B2' },
        },
      },
      {
        rect: {
          x: 3.61,
          y: 4.21,
          w: 5.98,
          h: 0.98,
          fill: { color: '#FFFFFF' },
        },
      },
      {
        text: {
          text: t('DownloadActionPlans.ActionPlanPinboard'),
          options: {
            x: 3.615,
            y: 4.27,
            fontSize: 10,
            bold: true,
            color: '#000000',
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${t('DownloadActionPlans.FileName')}:`,
          options: {
            x: 3.615,
            y: 4.47,
            fontSize: 8,
            bold: true,
            color: '#000000',
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${t('DownloadActionPlans.UploadDate')}:`,
          options: {
            x: 6.5,
            y: 4.47,
            fontSize: 8,
            bold: true,
            color: '#000000',
            fontFace: 'Delivery',
          },
        },
      },
      {
        text: {
          text: `${survey.name}_${t(
            'DownloadActionPlans.ActionPlanOverview',
          )}_${orgUnit.org_unit_id}_${orgUnit.name}`,
          options: {
            x: 0.24,
            y: '99%',
            fontSize: 10,
            color: '#000000',
            valign: 'bottom',
            fontFace: 'Delivery',
          },
        },
      },
    ],
  });

  createCoverSlide(
    pptx,
    generatedDate,
    survey,
    orgUnit,
    t('DownloadActionPlans.ActionPlanOverview').toUpperCase(),
    String(actionData.name),
    t,
  );

  actionData.actions.forEach((action, index) => {
    const actionSlide = pptx.addSlide({ masterName: 'SLIDE_MASTER' });
    const targetStatement = action.target_statement
      ? planActionStatus.value.target_statements.find(
          (statement) => statement.id === action.target_statement,
        ).label
      : '';
    const targetStatementSliced = targetStatement.slice(0, 82);
    const targetStatementWithDots =
      targetStatement.length > 82
        ? `${targetStatementSliced}...`
        : targetStatementSliced;
    const implementationDeadline = action.deadline
      ? new Date(action.deadline).toLocaleDateString(
          getCurrentLocale(),
          dateStringOptions,
        )
      : '';
    let progress = '';
    // Note: 0 (i.e. 0% progress) also needs to be considered
    if (action.progress !== null && action.progress !== undefined) {
      progress =
        action.progress === -1
          ? t('ActionPlan.ActionStatusDiscontinued')
          : `${action.progress}%`;
    }

    actionSlide.addText(
      `${orgUnit.org_unit_id} ${orgUnit.name} (${index + 1}/${
        actionData.actions.length
      })`,
      {
        x: 0.24,
        y: 0.86,
        fontSize: 18,
        color: '#d3050f',
        bold: true,
        fontFace: 'Delivery',
      },
    );

    actionSlide.addText(t('ActionPlan.Action'), {
      x: 3.5,
      y: 1.85,
      fontSize: 12,
      color: '#000000',
      bold: true,
      fontFace: 'Delivery',
    });

    actionSlide.addText(`${t('ActionPlan.ActionTitle')}: ${action.title}`, {
      x: 3.5,
      y: 2.05,
      fontSize: 8,
      color: '#000000',
      fontFace: 'Delivery',
    });

    actionSlide.addText(`${t('ActionPlan.ActionOwner')}: ${action.owner}`, {
      x: 3.5,
      y: 2.2,
      fontSize: 8,
      color: '#000000',
      fontFace: 'Delivery',
    });

    actionSlide.addText(
      `${t('DownloadActionPlans.EOSKPIStatement')}: ${targetStatementWithDots}`,
      {
        x: 3.5,
        y: 2.35,
        fontSize: 8,
        color: '#000000',
        fontFace: 'Delivery',
      },
    );

    actionSlide.addText(`${t('DownloadActionPlans.Details')}:`, {
      x: 3.5,
      y: 2.52,
      fontSize: 8,
      color: '#000000',
      fontFace: 'Delivery',
    });
    actionSlide.addText(`${action.notes.slice(0, 700)}`, {
      x: 3.5,
      y: 2.35,
      w: 6,
      h: '35%',
      fontSize: 8,
      color: '#000000',
      fontFace: 'Delivery',
    });

    actionSlide.addText(
      `${t('ActionPlan.ImplementationDeadline')}: ${implementationDeadline}`,
      {
        x: 6.5,
        y: 2.05,
        fontSize: 8,
        color: '#000000',
        fontFace: 'Delivery',
      },
    );

    actionSlide.addText(
      `${t('ActionPlan.ImplementationStatus')}: ${progress}`,
      {
        x: 6.5,
        y: 2.2,
        fontSize: 8,
        color: '#000000',
        fontFace: 'Delivery',
      },
    );

    const { dataUrl, height, width } = actionGraphsImg[action.id];
    actionSlide.addImage({
      data: dataUrl,
      x: 0.38,
      y: 2,
      w: width / DPI,
      h: height / DPI,
      sizing: {
        type: 'contain',
        x: 0,
        y: 0,
        w: 2.8,
        h: 2.8 * (height / width),
      },
    });

    action.attachments.slice(0, 3).forEach((attachment, ind) => {
      const attachmentNameLength = attachment.name.length;
      const shortenedName = attachment.name.slice(0, 36);
      const shortenedNameWithDots =
        attachmentNameLength > 36 ? `${shortenedName}...` : shortenedName;
      const created = new Date(attachment.created);
      actionSlide.addText(`${shortenedNameWithDots}.${attachment.extension}`, {
        x: 3.615,
        y: 4.6 + ind * 0.15,
        fontSize: 8,
        color: '#000000',
        fontFace: 'Delivery',
      });
      actionSlide.addText(
        created.toLocaleDateString(getCurrentLocale(), dateStringOptions),
        {
          x: 6.5,
          y: 4.6 + ind * 0.15,
          fontSize: 8,
          color: '#000000',
          fontFace: 'Delivery',
        },
      );
    });

    if (action.attachments.length > 3) {
      const attachmentsLengthAfterSlice = action.attachments.length - 3;
      const attachmentString =
        attachmentsLengthAfterSlice === 1 ? 'attachment' : 'attachments';
      actionSlide.addText(
        `+ ${attachmentsLengthAfterSlice} more ${attachmentString}`,
        {
          x: 3.615,
          y: 5.05,
          fontSize: 8,
          color: '#000000',
          fontFace: 'Delivery',
        },
      );
    }

    actionSlide.addText(`${index + 2}`, {
      x: '95%',
      y: '99%',
      fontSize: 10,
      color: '#000000',
      valign: 'bottom',
      fontFace: 'Delivery',
    });
  });

  // Create file and download it.
  pptx.writeFile({
    fileName: `planAction ActionPlan Overview ${orgUnit.org_unit_id}.pptx`,
  });
};
